import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { HostedAgentTemplate } from "@/api/admin-api"

import { getHostedAgentConflict } from "@/api/admin-api"
import { ArrowLeft, Bot, Sparkles } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Spinner } from "@/components/ui/spinner"

import { suggestHostedAgentSlug, validateHostedAgentSlug } from "../utils/hosted-agents"

type SelectedSource = { kind: "template"; template: HostedAgentTemplate } | { kind: "blank" }

type InstantiateHostedAgentDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  templates: HostedAgentTemplate[]
  isLoadingTemplates: boolean
  isPending: boolean
  /** Instantiate a template; overrides carry at least the (possibly edited) slug. */
  onInstantiate: (templateId: string, overrides: Record<string, unknown>) => Promise<void>
  /** Create an empty agent from scratch (name + slug). */
  onCreateBlank: (input: { name: string; slug: string }) => Promise<void>
}

export function InstantiateHostedAgentDialog({
  isOpen,
  onOpenChange,
  templates,
  isLoadingTemplates,
  isPending,
  onInstantiate,
  onCreateBlank,
}: InstantiateHostedAgentDialogProps) {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<SelectedSource | null>(null)
  const [slug, setSlug] = useState("")
  const [name, setName] = useState("")
  const [slugConflict, setSlugConflict] = useState(false)

  const resetState = () => {
    setSelected(null)
    setSlug("")
    setName("")
    setSlugConflict(false)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) resetState()
    onOpenChange(open)
  }

  const handleSelectTemplate = (template: HostedAgentTemplate) => {
    setSelected({ kind: "template", template })
    setSlug(template.default_slug)
    setSlugConflict(false)
  }

  const handleSelectBlank = () => {
    setSelected({ kind: "blank" })
    setSlug("")
    setName("")
    setSlugConflict(false)
  }

  const slugError = validateHostedAgentSlug(slug)
  const trimmedName = name.trim()
  const canSubmit =
    !isPending && !slugError && (selected?.kind !== "blank" || trimmedName.length > 0)

  const resolveSlugErrorText = (): string | null => {
    if (slugConflict) {
      return t("adminDashboard.hostedAgents.createDialog.slugConflict", {
        defaultValue: "This slug is already taken — pick another one.",
      })
    }
    if (!slug || !slugError) return null
    switch (slugError) {
      case "invalid":
        return t("adminDashboard.hostedAgents.createDialog.slugInvalid", {
          defaultValue: "Use lowercase letters, digits and interior hyphens only (e.g. “anbud-2”).",
        })
      case "reserved":
        return t("adminDashboard.hostedAgents.createDialog.slugReserved", {
          defaultValue: "This slug is reserved.",
        })
      case "too_long":
        return t("adminDashboard.hostedAgents.createDialog.slugTooLong", {
          defaultValue: "Slugs can be at most 63 characters.",
        })
      default:
        return null
    }
  }
  const slugErrorText = resolveSlugErrorText()

  const handleSubmit = async () => {
    if (!selected || slugError) return
    setSlugConflict(false)
    try {
      if (selected.kind === "template") {
        await onInstantiate(selected.template.template_id, { slug: slug.trim() })
      } else {
        await onCreateBlank({ name: trimmedName, slug: slug.trim() })
      }
      resetState()
    } catch (error) {
      if (getHostedAgentConflict(error)?.code === "slug_conflict") {
        setSlugConflict(true)
      }
      // Other errors: the owning card already toasted; stay open for retry.
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="size-5" />
            {t("adminDashboard.hostedAgents.createDialog.title", {
              defaultValue: "Create hosted agent",
            })}
          </DialogTitle>
          <DialogDescription>
            {selected
              ? t("adminDashboard.hostedAgents.createDialog.configureDescription", {
                  defaultValue: "Choose the address (slug) the agent will live under.",
                })
              : t("adminDashboard.hostedAgents.createDialog.chooseDescription", {
                  defaultValue: "Start from a platform role-agent template, or from scratch.",
                })}
          </DialogDescription>
        </DialogHeader>

        {!selected ? (
          <div className="space-y-2">
            {isLoadingTemplates ? (
              <>
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </>
            ) : (
              <>
                {templates.map((template) => {
                  const comingSoon = template.status === "coming_soon"
                  return (
                    <button
                      key={template.template_id}
                      className="hover:bg-accent/50 focus-visible:ring-ring w-full rounded-lg border p-4 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
                      type="button"
                      onClick={() => handleSelectTemplate(template)}
                    >
                      <div className="flex items-center gap-2">
                        <Sparkles className="text-muted-foreground size-4 shrink-0" />
                        <span className="font-medium">{template.name}</span>
                        {template.title ? (
                          <span className="text-muted-foreground text-xs">{template.title}</span>
                        ) : null}
                        {comingSoon ? (
                          <Badge variant="warning">
                            {t("adminDashboard.hostedAgents.createDialog.comingSoon", {
                              defaultValue: "Coming soon",
                            })}
                          </Badge>
                        ) : null}
                      </div>
                      <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                        {template.description}
                      </p>
                      <p className="text-muted-foreground mt-1 font-mono text-xs">
                        {template.default_slug}
                        {comingSoon
                          ? ` · ${t("adminDashboard.hostedAgents.createDialog.startsDisabled", {
                              defaultValue: "starts disabled",
                            })}`
                          : ""}
                      </p>
                    </button>
                  )
                })}
                <button
                  className="hover:bg-accent/50 focus-visible:ring-ring w-full rounded-lg border border-dashed p-4 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
                  type="button"
                  onClick={handleSelectBlank}
                >
                  <div className="flex items-center gap-2">
                    <Bot className="text-muted-foreground size-4 shrink-0" />
                    <span className="font-medium">
                      {t("adminDashboard.hostedAgents.createDialog.blankTitle", {
                        defaultValue: "Blank agent",
                      })}
                    </span>
                  </div>
                  <p className="text-muted-foreground mt-1 text-xs">
                    {t("adminDashboard.hostedAgents.createDialog.blankDescription", {
                      defaultValue:
                        "Start from scratch and configure everything yourself. The agent starts disabled with default budgets.",
                    })}
                  </p>
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {selected.kind === "template" ? (
              <div className="bg-muted/50 rounded-lg border p-3 text-sm">
                <span className="font-medium">{selected.template.name}</span>
                <span className="text-muted-foreground">
                  {" "}
                  · {selected.template.title || selected.template.template_id}
                </span>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="hosted-agent-create-name">
                  {t("adminDashboard.hostedAgents.createDialog.nameLabel", {
                    defaultValue: "Name",
                  })}
                </Label>
                <Input
                  id="hosted-agent-create-name"
                  value={name}
                  placeholder={t("adminDashboard.hostedAgents.createDialog.namePlaceholder", {
                    defaultValue: "e.g. Support Agent",
                  })}
                  onChange={(e) => {
                    const next = e.target.value
                    setName(next)
                    // Track the name until the admin edits the slug manually.
                    if (!slug || slug === suggestHostedAgentSlug(name)) {
                      setSlug(suggestHostedAgentSlug(next))
                      setSlugConflict(false)
                    }
                  }}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="hosted-agent-slug">
                {t("adminDashboard.hostedAgents.createDialog.slugLabel", { defaultValue: "Slug" })}
              </Label>
              <Input
                aria-invalid={Boolean(slugErrorText)}
                className="font-mono"
                id="hosted-agent-slug"
                placeholder="anbud"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value)
                  setSlugConflict(false)
                }}
              />
              {slugErrorText ? (
                <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs">
                  {slugErrorText}
                </p>
              ) : (
                <p className="text-muted-foreground text-xs">
                  {t("adminDashboard.hostedAgents.createDialog.slugHint", {
                    defaultValue:
                      "The agent's stable address — lowercase letters, digits and hyphens.",
                  })}
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {selected ? (
            <>
              <Button
                disabled={isPending}
                type="button"
                variant="outline"
                onClick={() => setSelected(null)}
              >
                <ArrowLeft className="mr-2 size-4" />
                {t("adminDashboard.hostedAgents.createDialog.back", { defaultValue: "Back" })}
              </Button>
              <Button disabled={!canSubmit} type="button" onClick={() => void handleSubmit()}>
                {isPending ? (
                  <>
                    <Spinner />
                    {t("adminDashboard.hostedAgents.createDialog.creating", {
                      defaultValue: "Creating...",
                    })}
                  </>
                ) : (
                  t("adminDashboard.hostedAgents.createDialog.create", {
                    defaultValue: "Create agent",
                  })
                )}
              </Button>
            </>
          ) : (
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t("adminDashboard.hostedAgents.createDialog.cancel", { defaultValue: "Cancel" })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
